/**
 * TanStack Query hooks for portal (student/parent) endpoints.
 * All hooks accept optional studentId (parent passes selectedChildId).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@client/lib/api-client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export interface PortalChild {
  id: string;
  fullName: string;
  branchId: string;
  branchName: string;
}

export interface PortalStudent {
  id: string;
  fullName: string;
  branchName?: string;
}

export interface PortalStats {
  activeClasses: number;
  attendanceRate: number | null;
  averageScore: number | null;
  dueAssignments: number;
}

export interface PortalDashboard {
  student: PortalStudent;
  children?: PortalChild[];
  stats: PortalStats;
  classes: Array<{
    id: string;
    name: string;
    courseName: string;
    nextSession: { weekday: string | null; startTime: string } | null;
  }>;
  upcomingAssignments: Array<{
    id: string;
    title: string;
    dueDate: string | null;
    submissionStatus: string;
  }>;
  latestReport: {
    id: string;
    periodLabel: string | null;
    overallRating: string | null;
    publishedAt: string | null;
  } | null;
}

export interface ScheduleSlot {
  classId: string;
  className: string;
  courseName: string;
  mode: 'weekly' | 'single';
  weekday: string | null;
  sessionDate: string | null;
  startTime: string;
  endTime: string;
  roomName: string;
  teacherName: string | null;
}

export interface PortalSchedule {
  student: PortalStudent;
  slots: ScheduleSlot[];
}

export interface GradeItem {
  id: string;
  gradeType: string;
  score: number;
  maxScore: number;
  feedback: string | null;
  gradedAt: string | null;
}

export interface ClassGrades {
  classId: string;
  className: string;
  courseName: string;
  averageScore: number | null;
  grades: GradeItem[];
}

export interface PortalGrades {
  student: PortalStudent;
  classes: ClassGrades[];
}

export interface PortalReportSummary {
  id: string;
  periodLabel: string | null;
  period: string;
  overallRating: string | null;
  publishedAt: string | null;
}

export interface PortalReportsList {
  student: PortalStudent;
  reports: PortalReportSummary[];
}

export interface TrendGrade {
  gradedAt: string | null;
  score: number;
  maxScore: number;
  label: string;
}

export interface TrendAttendance {
  label: string;
  rate: number;
}

export interface PortalComment {
  id: string;
  authorId: string;
  authorName: string | null;
  content: string;
  createdAt: string;
  editedAt: string | null;
}

export interface PortalReportDetail {
  report: {
    id: string;
    enrollmentId: string;
    period: string;
    periodLabel: string | null;
    periodStart: string | null;
    periodEnd: string | null;
    attendanceSummary: string | null;
    academicComment: string | null;
    behaviorComment: string | null;
    recommendation: string | null;
    overallRating: string | null;
    publishedAt: string | null;
    className: string;
    courseName: string;
  };
  trend: {
    grades: TrendGrade[];
    attendance: TrendAttendance[];
  };
  comments: PortalComment[];
}

export interface PortalAssignmentItem {
  assignmentId: string;
  title: string;
  type: string;
  dueDate: string | null;
  className: string;
  maxScore: number | null;
  submission: {
    id: string;
    status: string;
    submittedAt: string | null;
    score: number | null;
  } | null;
}

export interface PortalAssignments {
  student: PortalStudent;
  items: PortalAssignmentItem[];
}

export interface PortalAssignmentDetail {
  assignment: {
    id: string;
    title: string;
    description: string | null;
    type: string;
    dueDate: string | null;
    maxScore: number | null;
  };
  submission: {
    id: string;
    status: string;
    textContent: string | null;
    submittedAt: string | null;
    files: Array<{ id: string; fileName: string; fileSize: number; contentType: string }>;
    score: number | null;
    feedback: string | null;
  } | null;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------
export const portalKeys = {
  all: ['portal'] as const,
  children: () => ['portal', 'children'] as const,
  dashboard: (studentId?: string | null) => ['portal', 'dashboard', studentId] as const,
  schedule: (studentId?: string | null, weekStart?: string) =>
    ['portal', 'schedule', studentId, weekStart] as const,
  grades: (studentId?: string | null) => ['portal', 'grades', studentId] as const,
  reports: (studentId?: string | null) => ['portal', 'reports', studentId] as const,
  reportDetail: (reportId: string) => ['portal', 'reports', reportId, 'detail'] as const,
  assignments: (studentId?: string | null, status?: string) =>
    ['portal', 'assignments', studentId, status] as const,
  assignmentDetail: (assignmentId: string, studentId?: string | null) =>
    ['portal', 'assignments', assignmentId, 'detail', studentId] as const,
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------
export function usePortalChildren() {
  return useQuery({
    queryKey: portalKeys.children(),
    queryFn: () => api.get<PortalChild[]>('/api/portal/children'),
    staleTime: 5 * 60 * 1000,
  });
}

export function usePortalDashboard(studentId?: string | null) {
  return useQuery({
    queryKey: portalKeys.dashboard(studentId),
    queryFn: () =>
      api.get<PortalDashboard>('/api/portal/dashboard', studentId ? { studentId } : undefined),
    staleTime: 60 * 1000,
  });
}

export function usePortalSchedule(studentId?: string | null, weekStart?: string) {
  return useQuery({
    queryKey: portalKeys.schedule(studentId, weekStart),
    queryFn: () =>
      api.get<PortalSchedule>(
        '/api/portal/schedule',
        { ...(studentId ? { studentId } : {}), ...(weekStart ? { weekStart } : {}) },
      ),
    staleTime: 2 * 60 * 1000,
  });
}

export function usePortalGrades(studentId?: string | null) {
  return useQuery({
    queryKey: portalKeys.grades(studentId),
    queryFn: () =>
      api.get<PortalGrades>('/api/portal/grades', studentId ? { studentId } : undefined),
    staleTime: 2 * 60 * 1000,
  });
}

export function usePortalReports(studentId?: string | null) {
  return useQuery({
    queryKey: portalKeys.reports(studentId),
    queryFn: () =>
      api.get<PortalReportsList>('/api/portal/reports', studentId ? { studentId } : undefined),
    staleTime: 2 * 60 * 1000,
  });
}

export function usePortalReportDetail(reportId: string) {
  return useQuery({
    queryKey: portalKeys.reportDetail(reportId),
    queryFn: () => api.get<PortalReportDetail>(`/api/portal/reports/${reportId}`),
    enabled: !!reportId,
    retry: (failureCount, error) => {
      const e = error as { status?: number };
      if (e.status === 404 || e.status === 403) return false;
      return failureCount < 2;
    },
  });
}

export function usePortalAssignments(studentId?: string | null, status?: string) {
  return useQuery({
    queryKey: portalKeys.assignments(studentId, status),
    queryFn: () =>
      api.get<PortalAssignments>(
        '/api/portal/assignments',
        { ...(studentId ? { studentId } : {}), ...(status && status !== 'all' ? { status } : {}) },
      ),
    staleTime: 60 * 1000,
  });
}

export function usePortalAssignmentDetail(assignmentId: string, studentId?: string | null) {
  return useQuery({
    queryKey: portalKeys.assignmentDetail(assignmentId, studentId),
    queryFn: () =>
      api.get<PortalAssignmentDetail>(
        `/api/portal/assignments/${assignmentId}`,
        studentId ? { studentId } : undefined,
      ),
    enabled: !!assignmentId,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------
export function useCreatePortalComment(reportId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { content: string }) =>
      api.post<PortalComment>(`/api/progress-reports/${reportId}/comments`, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: portalKeys.reportDetail(reportId) });
    },
  });
}

export function useUpdatePortalComment(reportId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ commentId, content }: { commentId: string; content: string }) =>
      api.patch<PortalComment>(`/api/comments/${commentId}`, { content }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: portalKeys.reportDetail(reportId) });
    },
  });
}
